import library from './jokes.json';

const STORAGE_KEY = 'tt_jokes_state_v1';

const DEFAULT_CONFIG = {
  cooldownSeconds: 90,
  maxPerDay: 8,
  banterWeightFasih: 0.5,
  taskSpecificWeight: 2
};

function loadState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { lastShownAt: 0, dayCount: {}, recentIds: [] };
    return { lastShownAt: 0, dayCount: {}, recentIds: [], ...JSON.parse(raw) };
  } catch {
    return { lastShownAt: 0, dayCount: {}, recentIds: [] };
  }
}

function saveState(state) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch {}
}

function todayKey() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function bucketForSeconds(seconds) {
  const m = seconds / 60;
  if (m < 5) return 'speedrun';
  if (m < 30) return 'normal';
  if (m < 60) return 'solid';
  if (m < 120) return 'respectable';
  if (m < 240) return 'deepWork';
  return 'hubstaff';
}

function template(text, ctx) {
  // Wrap the punch variables — taskName, hours, minutes — in <em> markers.
  // The toast renders these in vermilion with an ink stroke for the manga
  // emphasis style. firstName and dayOfWeek stay plain.
  return text
    .replace(/\{firstName\}/g, ctx.firstName ?? '')
    .replace(/\{taskName\}/g, `<em>${ctx.taskName ?? 'task'}</em>`)
    .replace(/\{hours\}/g, `<em>${ctx.hours ?? ''}</em>`)
    .replace(/\{minutes\}/g, `<em>${ctx.minutes ?? ''}</em>`)
    .replace(/\{dayOfWeek\}/g, ctx.dayOfWeek ?? '');
}

function deriveUserKey(employee) {
  // Match the first name of the employee against the user profiles in the
  // library. Returns the matching key (e.g. "Fasih") or null for users
  // who are eligible but don't have a tailored profile (they'll only see
  // generic jokes).
  const name = (employee?.name || '').trim();
  if (!name) return null;
  const first = name.split(/\s+/)[0];
  const users = library.users || {};
  if (users[first]) return first;
  return null;
}

export function getConfig() {
  return { ...DEFAULT_CONFIG, ...(library.config || {}) };
}

export function getUserProfile(userKey) {
  if (!userKey) return null;
  return library.users?.[userKey] || null;
}

export function pickJoke({ employee, taskName, elapsedSeconds, now = Date.now() }) {
  if (!library.jokes || library.jokes.length === 0) return null;
  if (!Number.isFinite(elapsedSeconds) || elapsedSeconds < 0) return null;

  const config = getConfig();
  const state = loadState();

  // Cooldown — don't spam back-to-back completions.
  if (now - (state.lastShownAt || 0) < config.cooldownSeconds * 1000) return null;

  // Daily cap — protect agents from joke fatigue.
  const today = todayKey();
  const todayCount = state.dayCount?.[today] || 0;
  if (todayCount >= config.maxPerDay) return null;

  const bucket = bucketForSeconds(elapsedSeconds);
  const userKey = deriveUserKey(employee);
  const profile = getUserProfile(userKey);

  // Filter to jokes that fit this bucket and this user.
  let candidates = library.jokes.filter(j => {
    if (!Array.isArray(j.buckets) || !j.buckets.includes(bucket)) return false;

    const filter = j.userFilter || 'any';
    if (filter !== 'any' && filter !== userKey) return false;

    // Task-specific jokes only fire when the task matches.
    if (Array.isArray(j.tasks) && j.tasks.length > 0) {
      if (!j.tasks.includes(taskName)) return false;
    }

    // Style restriction: respectful_only users never see banter jokes.
    if (profile?.style === 'respectful_only' && j.style === 'banter') return false;

    return true;
  });

  if (candidates.length === 0) return null;

  // For mixed-style users (e.g. Fasih), throttle banter so it doesn't
  // dominate. Apply the banterWeight as a probability of *including*
  // banter in the eligible pool for this draw.
  if (profile?.style === 'mixed') {
    const banterWeight = profile.banterWeight ?? config.banterWeightFasih ?? 0.5;
    if (Math.random() > banterWeight) {
      const noBanter = candidates.filter(j => j.style !== 'banter');
      if (noBanter.length > 0) candidates = noBanter;
    }
  }

  // Avoid recent repeats — drop the last ~30 joke IDs from the pool when
  // we have enough alternatives.
  const recent = new Set(state.recentIds || []);
  if (candidates.length > 5) {
    const fresh = candidates.filter(j => !recent.has(j.id));
    if (fresh.length > 0) candidates = fresh;
  }

  // Weighted sampling — task-specific jokes get a 2× bump so the
  // tailored content shows up more often than the generics.
  const weighted = [];
  for (const j of candidates) {
    let w = j.weight || 1;
    const taskSpecific = Array.isArray(j.tasks) && j.tasks.length > 0;
    if (taskSpecific) w *= config.taskSpecificWeight ?? 2;
    for (let i = 0; i < w; i++) weighted.push(j);
  }

  if (weighted.length === 0) return null;

  const chosen = weighted[Math.floor(Math.random() * weighted.length)];

  // Format the text with template variables.
  const totalSeconds = Math.floor(elapsedSeconds);
  const minutes = Math.max(1, Math.round(totalSeconds / 60));
  const hours = totalSeconds >= 3600
    ? (Math.round((totalSeconds / 3600) * 10) / 10).toString().replace(/\.0$/, '')
    : '0';
  const firstName = (employee?.name || '').trim().split(/\s+/)[0] || '';
  const dayOfWeek = new Date().toLocaleDateString('en-US', { weekday: 'long' });

  const text = template(chosen.text, { firstName, taskName, minutes, hours, dayOfWeek });

  // Persist state — only count this draw once we're committing to show it.
  state.lastShownAt = now;
  state.dayCount = { [today]: todayCount + 1 }; // discard older days
  state.recentIds = [...(state.recentIds || []), chosen.id].slice(-30);
  saveState(state);

  return {
    id: chosen.id,
    text,
    bucket,
    style: chosen.style || 'general',
    userKey,
    raw: chosen.text
  };
}

export function jokesAvailableFor(employee) {
  return Boolean(employee?.welcomeEnabled);
}
